import { sanitizeDiagnosticUrl } from 'src/features/diagnostics/sanitize';

import './tracked-axios';
import { notifyHttpCall, type HttpCallEvent, beginHttpDetailCapture } from './http-observers';
import { beginApiCall, completeApiCall, type CompleteApiCallInput } from './api-call-history';
import { captureHttpBody, captureFetchBody, captureHttpHeaders } from './http-diagnostic-data';

export type TrackedFetchOptions = {
  sensitiveRequest?: boolean;
};

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.toString();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return String(input);
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method;
  return 'GET';
}

function completionForError(error: unknown): CompleteApiCallInput {
  const canceled =
    (typeof DOMException !== 'undefined' &&
      error instanceof DOMException &&
      error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError');
  return {
    status: canceled ? 'CANCELED' : 'NETWORK_ERROR',
    errorMessage: error instanceof Error ? error.message : String(error),
  };
}

function beginFetchCapture(input: RequestInfo | URL, init: RequestInit | undefined, url: string) {
  const allowed = beginHttpDetailCapture(url);
  if (!allowed) return null;
  try {
    const request = input instanceof Request ? input : null;
    const headers = new Headers(init?.headers ?? request?.headers);
    const requestHeaders = captureHttpHeaders(headers);
    let requestBody = Promise.resolve<unknown>(null);
    if (init?.body != null)
      requestBody = Promise.resolve(captureHttpBody(init.body, headers.get('content-type') ?? ''));
    else if (request) requestBody = captureFetchBody(request);
    return { allowed, requestHeaders, requestBody };
  } catch {
    return null;
  }
}

function notifyFetchCall(
  event: HttpCallEvent,
  capture: ReturnType<typeof beginFetchCapture>,
  response?: Response
): void {
  if (!capture?.allowed()) {
    notifyHttpCall(event);
    return;
  }
  try {
    const responseHeaders = response ? captureHttpHeaders(response.headers) : undefined;
    const responseUrl = response?.url ? sanitizeDiagnosticUrl(response.url) : undefined;
    // Launch before returning the original Response; never wait for the clone.
    const responseBody = response ? captureFetchBody(response) : Promise.resolve(undefined);
    Promise.all([capture.requestBody, responseBody])
      .then(([requestBody, body]) => {
        if (!capture.allowed()) return;
        notifyHttpCall({
          ...event,
          requestHeaders: capture.requestHeaders,
          requestBody,
          responseHeaders,
          responseBody: body,
          responseUrl,
        });
      })
      .catch(() => undefined);
  } catch {
    notifyHttpCall(event);
  }
}

/** Fetch-compatible first-party transport with a device-local diagnostic breadcrumb. */
export async function trackedApiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: TrackedFetchOptions = {}
): Promise<Response> {
  const method = requestMethod(input, init);
  const url = requestUrl(input);
  const startedAt = Date.now();
  const capture = beginFetchCapture(input, init, url);
  const id = beginApiCall({
    method,
    url,
    body: init?.body,
    sensitiveRequest: options.sensitiveRequest,
  });

  try {
    const response = await globalThis.fetch(input, init);
    const status = response.ok ? 'SUCCEEDED' : 'HTTP_ERROR';
    completeApiCall(id, { status, httpStatus: response.status });
    notifyFetchCall(
      {
        transport: 'fetch',
        method,
        url,
        status,
        httpStatus: response.status,
        durationMs: Date.now() - startedAt,
        errorCode: null,
        errorMessage: null,
        sensitive: options.sensitiveRequest === true,
      },
      capture,
      response
    );
    return response;
  } catch (error) {
    const completion = completionForError(error);
    completeApiCall(id, completion);
    notifyFetchCall(
      {
        transport: 'fetch',
        method,
        url,
        status: completion.status,
        httpStatus: null,
        durationMs: Date.now() - startedAt,
        errorCode: null,
        errorMessage: completion.errorMessage ?? null,
        sensitive: options.sensitiveRequest === true,
      },
      capture
    );
    throw error;
  }
}
